// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import { tokens } from '@hotjar/hotsauce';
import { getChallengeRequestExperiment } from '@hotjar/checkout/src/braintree/3ds';

/**
 * Service that executes the actual API calls to update a subscription.
 *
 * This service should ONLY be used by the Subscription and the ModalPaymentsV2 services.
 * All other controllers should use the Subscription service.
 */

/**
 * A preview of an updated subscription
 * @typedef {object} PreviewResult
 * @property {number} total_cost The total cost of the updated subscription
 */

import { PaymentMethod, VaultedPaymentMethod, ValidationError } from '@hotjar/webapp/billing/payment';

angular.module('hjApp').service('Payments', [
    '$q',
    '$rootScope',
    'Restangular',
    'RestangularV2',
    'Braintree',
    'Track',
    'UserData',
    'Feature',
    'Loader',
    function ($q, $rootScope, Restangular, RestangularV2, Braintree, Track, UserData, Feature, Loader) {
        /*************************
         * PRIVATE VARIABLES
         *************************/
        let threeDSecureInstance;
        let verify3dsStartTime;
        let hostedFieldsInstance;

        /*************************
         * PRIVATE FUNCTIONS
         *************************/

        /**
         * Return the pre-prepared Payment Details endpoint.
         * @param {number} accountId The account ID for the request endpoint.
         * @returns {Promise} The result of the call.
         */
        function rPaymentDetails(accountId) {
            return Restangular.one('accounts', accountId).one('payment').one('details');
        }

        /**
         * Return the pre-prepared Payment Details V2 endpoint.
         * @param {number} accountId The account ID for the request endpoint.
         * @returns {Promise} The prepared request.
         */
        function rPaymentDetailsV2(accountId) {
            return RestangularV2.one('accounts', accountId).one('payment').one('details');
        }

        /**
         * Prepared ClientToken endpoint.
         * @param {number} accountId The account ID to generate this token for.
         * @returns {Promise} The prepared request.
         */
        function rClientToken(accountId) {
            return Restangular.one('accounts', accountId).one('client-token');
        }

        /**
         * Return whether the transaction is allowed. This can occur in 3 cases:
         * 1. The bank waived through the transaction (and shifted liability to the bank).
         * 2. The bank issued a challenge the user successfully passed (shifting liability to the bank).
         * 3. The card is not enrolled in 3D Secure (liability remains with us).
         *
         * If the bank issued a challenge and the user did not pass it (they cancelled the flow, for example), the
         * liability is _not_ shifted to the issuing bank and attempting to pay may have a higher chance of failure.
         *
         * @param {object} threeDS The 3DS object with the liability shift information.
         * @param {boolean} threeDS.liabilityShiftPossible Whether the card is 3DS enrolled.
         * @param {boolean} threeDS.liabilityShifted Whether the bank was happy with the details provided.
         * @returns {boolean} Whether to allow the transaction.
         */
        function transactionAllowed(threeDS) {
            return !threeDS.liabilityShiftPossible || threeDS.liabilityShifted;
        }

        /**
         * Check if we accept a particular type of credit card.
         *
         * @param card Card object coming from a braintree payment form (fnOnCardChange).
         * @return true if the card should be accepted, false otherwise.
         */
        function isCardTypeAccepted(card) {
            return (
                // The card type hasn't been determined yet (it's probably empty)
                card.type == null ||
                _.includes(['american-express', 'discover', 'master-card', 'maestro', 'visa'], card && card.type)
            );
        }

        /**
         * Set braintree on a form.
         *
         * This will set up braintree using hosted fields (required so we can follow PCI DSS SAQ A).
         * Docs: https://developers.braintreepayments.com/guides/client-sdk/migration/javascript/v3
         *
         * @param {number} accountId The account ID to setup.
         * @returns {Promise} The promise to be resolved with the Braintree client.
         */
        function setupClient(accountId) {
            return rClientToken(accountId)
                .get()
                .then(({ token }) =>
                    Braintree.getClient().create({
                        authorization: token
                    })
                );
        }

        /**
         * Setup our Braintree validation function.
         *
         * @param {object} instance The hosted fields instance.
         * @param {Function} fnOnValidationError Any extra functionality to call if we get a valication error.
         * @returns {Promise} The promise with the Braintree Hosted Fields instance
         */
        function setValidationError(instance, fnOnValidationError) {
            instance.on('error', function (error) {
                var invalidFieldKeys = [];

                if (error.type === 'VALIDATION') {
                    // No 'details' object is returned if none of the fields are filled in.
                    if (typeof error.details === 'undefined') {
                        invalidFieldKeys = ['number', 'cvv', 'expirationMonth', 'expirationYear'];
                    } else {
                        invalidFieldKeys = error.details.invalidFieldKeys;
                    }

                    $rootScope.$apply(function () {
                        fnOnValidationError(invalidFieldKeys);
                    });
                }
            });
            return instance;
        }

        /**
         * Sets the focus to the specific hosted Braintree's field
         *
         * @param {string} field the field id, see:
         *
         * @see https://braintree.github.io/braintree-web/3.81.0/module-braintree-web_hosted-fields.html#~fieldOptions
         */
        this.setFocus = (field) => {
            hostedFieldsInstance?.focus(field);
        };

        /**
         * @returns instance Instance of Braintree's hosted fields
         */
        this.getHostedFieldsInstance = () => {
            return hostedFieldsInstance;
        };

        /**
         * Set a listener on Braintree for whenever the card valid state changes.
         *
         * @param {object} instance The hosted fields instance.
         * @param {Function} fnOnCardValidChange Run whenever the card type changes.
         * @returns {Promise} The promise with the Braintree Hosted Fields instance
         */
        function setCardValidChange(instance, fnOnCardValidChange) {
            instance.on('cardTypeChange', function (event) {
                var acceptedCard = _.chain(event.cards).filter(isCardTypeAccepted).first().value();
                if (acceptedCard) {
                    fnOnCardValidChange({
                        type: acceptedCard.niceType,
                        accepted: true
                    });
                } else if (event.cards && event.cards.length) {
                    fnOnCardValidChange({
                        type: _.first(event.cards).niceType,
                        accepted: false
                    });
                } else {
                    fnOnCardValidChange({
                        type: null,
                        accepted: true
                    });
                }
            });
            return instance;
        }

        /**
         * Setup the 3D Secure instance.
         * @param {number} accountId The account to get 3DS for.
         * @returns {Promise} The promise resolving to the 3D Secure instance.
         */
        function setup3DSInstance(accountId) {
            /**
             * Only one instance of 3ds can be instantiated for particular user,
             *  since user can be account owner of one account
             * We cache the instance mainly due to the fact that events are emitted multiple times,
             *  when multiple instances are created for one client
             */
            if (threeDSecureInstance) {
                return $q.resolve(threeDSecureInstance);
            }

            return setupClient(accountId).then((client) => {
                return Braintree.get3DSecure()
                    .create({
                        client: client,
                        version: 2
                    })
                    .then((instance) => {
                        instance.on('authentication-modal-close', () => {
                            Track.authenticationModalClosed();
                        });

                        instance.on('authentication-modal-render', () => {
                            Track.authenticationModalRendered();
                            // measure how much time it takes from starting the authentication flow until
                            //  the 3DS modal challenge is rendered
                            window.datadogRum.addAction('display-authenticate-challenge', {
                                executionTime: Math.floor(window.performance.now() - verify3dsStartTime)
                            });
                        });

                        instance.on('customer-canceled', () => {
                            Track.authenticationModalCustomerCancelled();
                        });

                        threeDSecureInstance = instance;

                        return instance;
                    });
            });
        }

        /**
         * Decorator for handling Restangular responses which are resolved with
         * a `200 - success: false` such that we reject the promise rather than
         * resolve it.
         *
         * If `success` isn't present in the response, or is falsey, we reject
         * the promise, if its truthy, we call the provided callback. If no
         * callback is provided, we simply return the response object.
         *
         * In both resolved and rejected paths, the response object is stripped
         * of its Restangular properties.
         *
         * @param {Function} [fn] The function to call on success.
         */
        function stripAndRejectErrors(fn) {
            fn = fn || _.identity;
            return function (res) {
                res = Restangular.stripRestangular(res);
                if (!res.success) {
                    return $q.reject(res);
                }
                return fn(res);
            };
        }

        /**
         * Verify the card, if necessary.
         *
         * This will determine whether a 3D Secure verification is necessary and request it from the bank. If the bank
         * deems it necessary to show a 3DS page, we will display this to the end user.
         *
         * This method also handles the response to the 3DS challenge. If the challenge was successful, we resolve with
         * the new payment method (and updated nonce) from the 3DS challenge. If it was not (usually because the user
         * hit cancel), then we return a rejected promise containing the updated payment method. This payment method
         * will still have an updated nonce. We can use this to attempt to perform a transaction, however it has a
         * higher risk of being rejected by the bank.
         *
         * If we pass in a total value of "0.00", then we do not attempt to 3DS verify the transaction and just return
         * the payment method we passed in.
         * @deprecated
         * @use [`verify3DS`](/packages/checkout/src/braintree/3ds.ts)
         *
         * @param {object} instance The Braintree 3DS instance.
         * @param {VaultedPaymentMethod} vaultedPaymentMethod The payment method from Braintree.
         * @param {Decimal} total The total amount to authenticate with the bank.
         * @param {object} account The account on what to check if it needs to force 3ds challenge
         * @returns {Promise<VaultedPaymentMethod>} The new payment method after 3DS verification has completed.
         */
        function verify3DS(instance, vaultedPaymentMethod, total, account) {
            const shouldRequestChallenge = Feature.isAccountAllowed('force_3ds_challenge', account);

            if (!total.eq(0)) {
                Track.verify3DSCalled(total, vaultedPaymentMethod.bin);
                verify3dsStartTime = window.performance.now();
                let requiresUserAuthentication;

                return getChallengeRequestExperiment(account, shouldRequestChallenge).then((challengeRequested) => {
                    return instance
                        .verifyCard({
                            amount: total.toFixed(2),
                            nonce: vaultedPaymentMethod.nonce,
                            bin: vaultedPaymentMethod.bin,
                            challengeRequested,
                            onLookupComplete: (data, next) => {
                                Track.payment3DS2AuthenticationLookup(
                                    data?.paymentMethod?.threeDSecureInfo?.status,
                                    data?.paymentMethod?.details?.bin,
                                    data?.paymentMethod?.threeDSecureInfo?.threeDSecureVersion
                                );
                                requiresUserAuthentication = data?.requiresUserAuthentication;
                                next();
                            }
                        })
                        .then(function ({ nonce, threeDSecureInfo }) {
                            // NOTE Even though we waive through the error above, we still reject the promise if this
                            // call fails, since it is still a 3DS failure that we might want to handle differently in
                            // future.
                            const verifiedPaymentMethod = vaultedPaymentMethod.getVerifiedPaymentMethod(nonce);
                            Track.verify3DSChallenge(
                                total,
                                threeDSecureInfo.liabilityShiftPossible,
                                threeDSecureInfo.liabilityShifted,
                                threeDSecureInfo.threeDSecureVersion
                            );

                            const authenticated = transactionAllowed(threeDSecureInfo);

                            window.datadogRum.addAction('authenticate-card', {
                                authenticated,
                                executionTime: Math.floor(window.performance.now() - verify3dsStartTime),
                                threeDSChallengeRequired: !!requiresUserAuthentication
                            });

                            return authenticated ? verifiedPaymentMethod : $q.reject(verifiedPaymentMethod);
                        });
                });
            }
            // 3DS isn't required (and can't be verified anyway) on 0-value subscriptions. In this case, the user is
            // probably churning out so we want the upsert to continue.
            return $q.resolve(vaultedPaymentMethod);
        }

        /************
         * DATA TYPES
         ***********/
        /**
         *
         * @param {object} paymentMethod The payment method from the server.
         * @returns {VaultedPaymentMethod} The parsed response.
         */
        function parsePaymentDetailsResponse({ token, nonce, bin, customer_card_info, payment_type }) {
            customer_card_info =
                _.isNull(customer_card_info) || _.isUndefined(customer_card_info)
                    ? {
                          cardholder_name: null,
                          locality: null,
                          postal_code: null,
                          country_code_alpha2: null
                      }
                    : customer_card_info;
            return new VaultedPaymentMethod(
                nonce,
                bin,
                payment_type,
                customer_card_info.cardholder_name,
                customer_card_info.country_code_alpha2,
                customer_card_info.locality,
                customer_card_info.postal_code,
                token
            );
        }

        /**
         * Serialize a PaymentMethod into request output to be vaulted.
         * @param {PaymentMethod} paymentMethod The payment method to serialize.
         * @returns {object} The serialized payment method.
         */
        function serializePaymentDetails(paymentMethod) {
            return {
                payment_method_nonce: paymentMethod.nonce,
                payment_type: paymentMethod.paymentType,
                cardholder_name: paymentMethod.cardholderName,
                country_code_alpha2: paymentMethod.countryCode,
                locality: paymentMethod.city,
                postal_code: paymentMethod.postCode
            };
        }

        /*************************
         * PUBLIC ACCESS FUNCTIONS
         *************************/

        /**
         * Get an account's pro rata details.
         *
         * @param {number} accountId ID of account this affects.
         * @param {SitePlanInfo[]} costSpecs Cost Specs this affects.
         * @param {'monthly'|'yearly_recurring'} billingCycle Either
         * @returns {Promise} The pro-rated result.
         */
        this.getProRata = function (accountId, costSpecs, billingCycle) {
            return rPaymentDetails(accountId).customPOST({
                action: 'calculate_pro_rata',
                cost_specs: costSpecs,
                billing_cycle: billingCycle
            });
        };

        /**
         * Get an account's pro rata details.
         *
         * @param {number} accountId ID of account this affects.
         * @param {number} sampleRate The sample rate for the potential new site.
         * @param {number} organization Organization id where the new site will belong to.
         * @returns {Promise} The result of calculating pro-rata for a new site.
         */
        this.getProRataNewSite = function (accountId, planId, organization) {
            return rPaymentDetails(accountId).customPOST({
                action: 'calculate_pro_rata_new_site',
                plan_id: planId,
                organization_id: organization
            });
        };

        /**
         * Get an account's payment details.
         *
         * @param {number} accountId ID of account this affects.
         * @returns {Promise} The response with the account's payment details.
         */
        this.getDetails = function (accountId) {
            return rPaymentDetails(accountId).get().then(stripAndRejectErrors());
        };

        /**
         * Vault the given payment method with Braintree and return a new Payment Method with a new nonce to verify
         * against 3D Secure.
         *
         * @param {number} accountId The account ID to vault the card to.
         * @param {PaymentMethod} paymentMethod The payment method to vault.
         * @returns {Promise<VaultedPaymentMethod>} Resolve to the newly vaulted payment method.
         */
        this.vaultCard = function vaultCard(accountId, paymentMethod) {
            var postData = serializePaymentDetails(paymentMethod);
            return rPaymentDetailsV2(accountId)
                .customPOST(postData)
                .then(stripAndRejectErrors())
                .then(parsePaymentDetailsResponse);
        };

        /**
         * Get the active payment method.
         *
         * @param {object} account The account object.
         * @returns {Promise<PaymentMethod>} Resolve to the stored payment method.
         */
        this.getPaymentMethod = function getPaymentMethod(account) {
            return rPaymentDetailsV2(account.id).get().then(stripAndRejectErrors()).then(parsePaymentDetailsResponse);
        };

        /**
         * Authenticate the given payment method against the calculated subscription value.
         *
         * This will only attempt to authenticate credit cards. All other payment methods will simply pass through this
         * function as they are either authenticated elsewhere (in the case of PayPal) or authentication isn't needed
         * (bank transfer).
         *
         * @param {object} account The account to authenticate for.
         * @param {VaultedPaymentMethod} vaultedPaymentMethod The payment method to authenticate.
         * @param {number} amount The amount to be charged
         * @returns {Promise<VaultedPaymentMethod>} The payment method after it passes through authentication.
         */
        this.authenticatePaymentMethod = function authenticatePaymentMethod(account, vaultedPaymentMethod, amount) {
            const isAuthenticationNeeded = Feature.isAccountAllowed('billing_3ds2', account);

            Track.authenticationNeeded(isAuthenticationNeeded);

            if (!isAuthenticationNeeded) {
                // 3ds not enabled for this account yet
                return $q.resolve(vaultedPaymentMethod);
            }

            if (vaultedPaymentMethod.paymentType !== 'credit_card') {
                // We only support authenticating credit card payment methods.
                // Other payment method types are either handled by different
                // methods or are excluded from authentication.
                return $q.resolve(vaultedPaymentMethod);
            }

            const authenticateRequest = setup3DSInstance(account.id)
                .then(
                    (braintreeInstance) => verify3DS(braintreeInstance, vaultedPaymentMethod, amount, account),
                    // If any of the pre-requisite steps fail, simply
                    // resolve with the given payment method.
                    //
                    // This allows the flow to continue, just without
                    // 3DS verification.
                    () => vaultedPaymentMethod
                )
                // If 3DS fails to verify, simply resolve with the resulting
                // payment method. This is needed as `verify3DS` consumes
                // the nonce held within `vaultedPaymentMethod` and returns a new
                // usable nonce, even if the verification failed.
                //
                // This allows the flow to continue, just without 3DS
                // verification.
                .then(undefined, (paymentMethod) => paymentMethod);
            Loader.show('Contacting bank...', 0, undefined, authenticateRequest);

            return authenticateRequest;
        };

        /**
         * Upsert (Update or insert - https://en.wiktionary.org/wiki/upsert) a new braintree customer
         * and a new Subscription on Braintree for an account.
         *
         * @param {object} account Account that is affects.
         * @param {SitePlanInfo[]} costSpecs Array of CostSpec tuples.
         * @param {PaymentMethod} [paymentMethod] The payment method to use to create the subscription.
         * if undefined, attempt to use the existing payment method on the account.
         * @param {'monthly' | 'yearly_recurring'} [billingCycle] The billing cycle to change to.
         * @param {string} [poNumber] If a manual invoice is to be generated, set its PO Number.
         */
        this.upsertSubscription = function upsertSubscription(
            account,
            costSpecs,
            paymentMethod = undefined,
            billingCycle = undefined,
            poNumber = undefined
        ) {
            return rPaymentDetails(account.id)
                .customPOST({
                    action: 'upsert_subscription',
                    cost_specs: costSpecs,
                    billing_cycle: billingCycle,
                    payment_method_type: paymentMethod && paymentMethod.paymentType,
                    payment_method_nonce: paymentMethod && paymentMethod.nonce,
                    payment_method_token: paymentMethod && paymentMethod.token,
                    po_number: poNumber
                })
                .then(
                    stripAndRejectErrors((response) => {
                        // We refresh user data because it's cached in localstorage
                        //  and it needs to be updated even when whole page is refreshed
                        return $q((resolve) => UserData.refreshFromServer(() => resolve(response)));
                    })
                );
        };

        /**
         * The payment terms of a payment method (credit or debit)
         * @typedef {object} PaymentTerms
         */
        var PaymentTerms = {
            DEBIT: 'debit'
        };

        /**
         * The request data when adding a new PaymentMethod via a nonce
         * @typedef {object} PaymentDetailsV1RequestData
         * @property {string} payment_method_nonce The nonce which refers to the
         * given payment method
         * @property {boolean} update_subscription Should we attempt to update
         * the subscription cost
         * @property {string} payment_terms Is this a debit (card/paypal)
         * payment method or bank transfer
         */
        function PaymentDetailsV1RequestData(nonce) {
            return {
                payment_method_nonce: nonce,
                update_subscription: false,
                payment_terms: PaymentTerms.DEBIT
            };
        }

        /**
         * Create a new payment method for a Braintree customer and update the account to start using it.
         *
         * @param {number} accountId Account id to update payment method for.
         * @param {number} paymentMethodNonce Nonce generated by braintree.
         * @param {boolean} updatingPlans Whether we are updating plans at the same time.
         * @param {string} cardholderName The cardholder's name.
         * @param {string} countryCode The country's 2-character code.
         * @param {string} city The city the cardholder is registered to.
         * @param {string} postCode The post code of the cardholder.
         * @returns {Promise} The result of updating the payment method.
         */
        this.updatePaymentMethod = function updatePaymentMethod(accountId, nonce) {
            var putData = PaymentDetailsV1RequestData(nonce);

            return rPaymentDetails(accountId).customPUT(putData).then(stripAndRejectErrors());
        };

        /**
         * Delete a customer's payment method from Braintree.
         *
         * @param {number} accountId Account id to update payment method for.
         * @returns {Promise} The result of removing the payment method.
         */
        /**
         *
         * @deprecated
         * @use [`paymentMethodAPI.remove`](/packages/legacy/src/webapp/domains/billing/paymentMethod.ts)
         */
        this.deletePaymentMethod = function (accountId) {
            return rPaymentDetails(accountId)
                .customPOST({
                    action: 'remove_payment_method'
                })
                .then(function (res) {
                    var deferred = $q.defer();
                    if (res.success) {
                        UserData.refreshFromServer(_.partial(deferred.resolve, res));
                    } else {
                        deferred.reject(res);
                    }
                    return deferred.promise;
                });
        };

        /**
         * Setup the Braintree Hosted Fields instance.
         *
         * @param {number} accountId The account's ID.
         * @param {string} formId The base id of the form fields. This will then look for #<formId>-number #<formId>-cvv
         * #<formId>-expiration-month and #<formId>-expiration-year selectors.
         * @param {Function} fnOnValidationError The function that Braintree will call if the form validated
         * incorrectly.
         * @param {Function} fnOnCardValidChange The function that Braintree will call if the card become valid/invalid.
         * @returns {Promise} A promise resolving to the Braintree Hosted Fields instance.
         */
        this.setupHostedFields = function setupHostedFields(
            accountId,
            formId,
            fnOnValidationError,
            fnOnCardValidChange
        ) {
            return setupClient(accountId).then(function (client) {
                // TODO: Improve as specified here if you touch this
                //  https://medium.com/braintree-product-technology/faster-hosted-fields-697be183e54c
                return Braintree.getHostedFields()
                    .create({
                        client: client,
                        styles: {
                            input: {
                                'font-size': '14px',
                                color: tokens.color.text_default
                            },
                            '::-webkit-input-placeholder': {
                                color: tokens.color.text_disabled
                            },
                            ':-moz-placeholder': {
                                color: tokens.color.text_disabled
                            },
                            '::-moz-placeholder': {
                                color: tokens.color.text_disabled
                            },
                            ':-ms-input-placeholder': {
                                color: tokens.color.text_disabled
                            }
                        },

                        fields: {
                            number: {
                                selector: `#${formId}-number`
                            },
                            cvv: {
                                selector: `#${formId}-cvv`
                            },
                            expirationMonth: {
                                selector: `#${formId}-expiration-month`,
                                placeholder: 'MM'
                            },
                            expirationYear: {
                                selector: `#${formId}-expiration-year`,
                                placeholder: 'YYYY'
                            }
                        }
                    })
                    .then((instance) => {
                        hostedFieldsInstance = instance;
                        return hostedFieldsInstance;
                    })
                    .then(_.partialRight(setValidationError, fnOnValidationError))
                    .then(_.partialRight(setCardValidChange, fnOnCardValidChange));
            });
        };

        /**
         * Setup the Paypal integration for Braintree.
         *
         * @param {number} accountId The account's ID.
         * @param {string} paypalButtonId The div id to contain the Paypal form.
         * @param {Function} fnOnPaypalLogin callback for Paypal login completion.
         * @returns {Promise} The promise with the Braintree Hosted Fields instance.
         */
        this.setupPaypal = function setupPaypal(accountId, paypalButtonId, fnOnPaypalLogin, style = {}) {
            return setupClient(accountId).then(function (client) {
                Braintree.getPaypalCheckout()
                    .create({
                        client: client
                    })
                    .then(function (instance) {
                        return Braintree.getPaypal()
                            .Buttons({
                                createBillingAgreement: function () {
                                    return instance.createPayment({
                                        flow: 'vault'
                                    });
                                },

                                onApprove: function (data) {
                                    return instance.tokenizePayment(data).then(fnOnPaypalLogin);
                                },
                                style
                            })
                            .render(`#${paypalButtonId}`)
                            .then(function () {
                                return instance;
                            });
                    });
            });
        };

        /**
         * Submit the credit card details to Braintree. This should only be used for credit card and not Paypal.
         * Paypal has its own internal submission system that is hidden from here.
         *
         * @param {Object} instance The Hosted Fields instance to submit the card details for.
         * @param {Function} instance.tokenize The tokenize function that will resolve.
         * @param {Object} [options={}] Extra options to submit.
         * @param {string} [options.cardholderName] The cardholder's name.
         * @param {string} [options.city] The city.
         * @param {string} [options.postCode] The post-code.
         * @param {string} [options.countryCode] The 2-letter country code.
         * @returns {Promise<PaymentMethod>} The promise of completion with a payment method
         * (https://braintree.github.io/braintree-web/3.50.1/HostedFields.html#~tokenizePayload)
         */
        this.submit = function submit(instance, options) {
            options = options || {};

            return (instance ?? hostedFieldsInstance)
                .tokenize({
                    cardholderName: options.cardholderName,
                    billingAddress: {
                        locality: options.city,
                        postalCode: options.postCode,
                        countryCodeAlpha2: options.countryCode
                    }
                })
                .then(
                    ({ nonce, details }) =>
                        new PaymentMethod(
                            nonce,
                            details.bin,
                            'credit_card',
                            options.cardholderName,
                            options.countryCode,
                            options.city,
                            options.postCode
                        ),
                    ({ code, details }) => {
                        let errorFields = [];

                        if (code === 'HOSTED_FIELDS_FIELDS_INVALID') {
                            errorFields = details.invalidFieldKeys;
                        } else if (code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
                            errorFields = ['number', 'cvv', 'expirationMonth', 'expirationYear'];
                        }
                        return $q.reject(new ValidationError('Invalid Card', errorFields));
                    }
                );
        };

        /**
         * Create a new Customer, assign them a PaymentMethod and create a Subscription.
         *
         * @param {number} accountId ID of account this affects.
         * @param {SitePlanInfo[]} costSpecs Array of CostSpec tuples.
         * @param {string} paymentMethodNonce Nonce generated by braintree.
         */
        this.createCustomerPaymentMethodAndSubscription = function (accountId, costSpecs, paymentMethodNonce) {
            return rPaymentDetails(accountId)
                .customPOST({
                    action: 'create_customer_payment_method_and_subscription',
                    cost_specs: costSpecs,
                    payment_method_nonce: paymentMethodNonce
                })
                .then(function (res) {
                    var deferred = $q.defer();
                    if (res.success) {
                        UserData.refreshFromServer(_.partial(deferred.resolve, res));
                    } else {
                        deferred.reject(res);
                    }
                    return deferred.promise;
                });
        };

        /*************************
         * ERROR HANDLING
         *************************/

        /**
         * Get info for a payment error code.
         *
         * Error message descriptions can be obtained from:
         * https://articles.braintreepayments.com/control-panel/transactions/declines
         *
         * @param {string} errorMessage The error message we are returned when a payment failure occurs.
         * @returns {string} The user-friendly error message.
         */
        this.errorInfo = function (errorMessage) {
            switch (errorMessage) {
                /* GENERIC */
                case 'CVV must be 4 digits for American Express and 3 digits for other card types.':
                    return 'CVC must be 3 digits long or 4 digits for American Express cards.';

                case 'Processor Network Unavailable - Try Again':
                    return (
                        'We encountered an issue when processing this transaction, please try again. ' +
                        'If the problem persists please contact us for assistance.'
                    );

                /* SOFT DECLINES – Declines which usually can be easily resolved, at times even by just retrying. */
                case 'Declined':
                case 'Do Not Honor':
                case 'Issuer or Cardholder has put a restriction on the card':
                case 'Processor Declined':
                    return (
                        'Your bank has declined this transaction, please try again. ' +
                        'To know the exact reason and how to resolve this, you will need to contact your bank.'
                    );

                case 'Payer Cannot Pay For This Transaction With PayPal':
                    return (
                        'PayPal has declined this transaction. To know the exact reason and ' +
                        'how to resolve this you will need to contact PayPal support.'
                    );

                /* HARD DECLINES – Declines which usually are unlikely to be resolved. */
                case 'Card Issuer Declined CVV':
                case 'No Account':
                    return (
                        'You have either entered an invalid CVC code or made a typo in the details you submit. ' +
                        'Please try again. If the problem persist you will need to contact your bank.'
                    );

                case 'Call Issuer. Pick Up Card.':
                    return (
                        'Your bank has declined this transaction due to the card being reported as lost or stolen. ' +
                        'For more information you will need to contact your bank.'
                    );

                case 'Declined - Call Issuer':
                    return (
                        'Your bank has declined this transaction. ' +
                        'To know the exact reason and how to resolve this, you will need to contact your bank.'
                    );

                case 'Expired Card':
                    return 'The card used appears to be expired. Please use an alternative payment method.';

                case 'Funding Instrument In The PayPal Account Was Declined By The Processor Or Bank, ' +
                    "Or It Can't Be Used For This Payment":
                    return (
                        'The payment method associate with your PayPal account was declined. ' +
                        'Please update the details in you PayPal account or choose another payment method.'
                    );

                case 'Insufficient Funds':
                    return (
                        'Your bank has declined this transaction due to insufficient funds. ' +
                        'For more information you will need to contact your bank.'
                    );

                case 'Invalid Transaction':
                    return (
                        'Your bank has declined this transaction probably because the card you used ' +
                        "doesn't support this type of transaction. You will need to contact your " +
                        'bank for more information.'
                    );

                case 'Transaction Not Allowed':
                    return (
                        'Your bank has declined this transaction for unspecified reasons. ' +
                        'You will need to either contact your bank or use an alternative payment method.'
                    );

                case 'Voice Authorization Required':
                    return (
                        'This card cannot be used for this transaction as it requires an authorization code ' +
                        "obtained over the phone. Unfortunately we don't support this. " +
                        'Please use an alternative payment method.'
                    );
                case 'Payment method token is invalid.':
                case 'Payment method nonce is invalid.':
                    return (
                        'We tried charging your current payment method but the charge was declined. ' +
                        'Please provide details for a new payment method below.'
                    );
                case 'Billing cycle not supported.':
                    return (
                        'Your billing cycle does not support payments through the Hotjar interface directly. ' +
                        'Please contact support through the Help menu so we can help settle the payment.'
                    );
                case 'Cardholder Authentication Required':
                    return (
                        '3-D Secure authentication is required to process your payment. ' +
                        'Please re-enter your payment details to continue.'
                    );
                case '3D Secure enriched payment method nonce did not authenticate successfully.':
                    return (
                        '3-D Secure authentication is required to process your payment. If you are unable to do ' +
                        'this, please use a different payment method.'
                    );
                /* DEFAULT – Return a generic error message. */
                default:
                    return (
                        "We're sorry but we encountered an issue when processing your payment. " +
                        'Please contact support through the Help menu for assistance.'
                    );
            }
        };
    }
]);
